import { AfterViewInit, Component, effect, inject, Injector, OnDestroy, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AvatarNameModule } from '@vueling-ng/vy-ds/src/lib/components/globals/avatar-name';
import { BoxLevelEnum, BoxModule } from '@vueling-ng/vy-ds/src/lib/components/globals/box';
import { ButtonModule, ButtonSizeEnum, ButtonTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/button';
import { ChipModule } from '@vueling-ng/vy-ds/src/lib/components/globals/chip';
import { LoaderComponent, LoaderModule } from '@vueling-ng/vy-ds/src/lib/components/globals/loaders/loader';
import { MenuHorizontalComponent } from '@vueling-ng/vy-ds/src/lib/components/globals/menu-horizontal';
import { PaginatorModule } from '@vueling-ng/vy-ds/src/lib/components/globals/paginator';
import {
  SegmentedControlsComponent,
  SegmentedControlsModel
} from '@vueling-ng/vy-ds/src/lib/components/globals/segmented-controls';
import { SortEvent } from '@vueling-ng/vy-ds/src/lib/components/globals/table';
import { Subject, takeUntil } from 'rxjs';
import { BookingTableComponent } from '../booking-list/components/booking-table/booking-table.component';
import { ExportBookingListComponent } from '../booking-list/components/export-booking-list/export-booking-list.component';
import { creditShellHeaders, headerToSortMap, TableHeaderId } from '../booking-list/models/booking-table-header.model';
import { EmptyImg } from '../booking-list/models/booking-type';
import {
  BookingsParams,
  defaultPageSize,
  defaultSortCriteria,
  Filters,
  SortColumn,
  SortCriteria
} from '../booking-list/models/bookings.interface';
import { QuickFilters, quickFiltersCredit, QuickFiltersValue } from '../booking-list/models/filters.model';
import { BookingListFilterService } from '../booking-list/services/booking-list-filter.service';
import { BookingListService } from '../booking-list/services/booking-list.service';

@Component({
  selector: 'app-booking-credit-fly',
  templateUrl: './booking-credit-fly.component.html',
  styleUrl: './booking-credit-fly.component.scss',
  standalone: true,
  imports: [
    MenuHorizontalComponent,
    AvatarNameModule,
    TranslateModule,
    BoxModule,
    SegmentedControlsComponent,
    ButtonModule,
    ChipModule,
    ExportBookingListComponent,
    LoaderModule,
    BookingTableComponent,
    PaginatorModule
  ]
})
export class BookingCreditFlyComponent implements AfterViewInit, OnDestroy {
  @ViewChild(LoaderComponent) private readonly loader!: LoaderComponent;

  private readonly _bookingListService = inject(BookingListService);
  private readonly _bookingListFilterService = inject(BookingListFilterService);
  private readonly _injector = inject(Injector);

  public readonly bookingList = this._bookingListService.currentViewModelBooking();
  public readonly ButtonType = ButtonTypeEnum;
  public readonly ButtonSize = ButtonSizeEnum;
  public readonly BoxLevel = BoxLevelEnum;
  public readonly quickFilters: SegmentedControlsModel[] = quickFiltersCredit;
  public readonly notFoundBookingsImage = EmptyImg;
  private startPage = 1;
  private currentSelectFilter: Filters = { status: QuickFiltersValue.None };
  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  public ngAfterViewInit(): void {
    this._bookingListService.setTableHeaders(creditShellHeaders);
    effect(
      () => {
        this.currentSelectFilter = {
          ...this.currentSelectFilter,
          status: this._bookingListFilterService.getCreditQuickFilter()()
        };
        this.loadBookings(defaultSortCriteria, this.currentSelectFilter);
      },
      { injector: this._injector }
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public changeQuickFilter(segmentedControl: SegmentedControlsModel): void {
    if (!this.loader) {
      return;
    }
    this._bookingListFilterService.changeCreditQuickFilter(segmentedControl.value as QuickFilters);
  }

  public onSort(event: SortEvent): void {
    const sortCriteria: SortCriteria = {
      column: this.getSortColumn(event.column as TableHeaderId),
      direction: event.direction
    };

    this.loadBookings(sortCriteria, this.currentSelectFilter);
  }

  public changePage(pageNumber: number): void {
    this.startPage = pageNumber;
    this.loadBookings(defaultSortCriteria, this.currentSelectFilter);
  }

  public startLoading(): void {
    this.loader.loadingProcess();
  }

  public stopLoading(): void {
    this.loader.loadingDone();
  }

  private getSortColumn(header: TableHeaderId): SortColumn {
    return headerToSortMap[header] ?? 'None';
  }

  private loadBookings(sortCriteria: SortCriteria, filters: Filters): void {
    const bookingParams: BookingsParams = {
      pageNumber: this.startPage,
      pageSize: defaultPageSize,
      sortCriteria,
      filters
    };

    this.startLoading();

    this._bookingListService
      .updateCreditBookings(bookingParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.stopLoading();
      });
  }
}
